import { ReactNode, useCallback, useMemo } from 'react';
import { useChainId } from 'wagmi';
import { TextLabel2 } from '@cbhq/cds-web/typography';
import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { memo } from 'react';
import { useLiquidityChartData } from './useLiquidityChartData';
import { DepthChart } from './DepthChart';
import { Bound, ZoomLevels } from './types';
import type { PoolKey, PoolState } from '@/onchain/pools';

export function DepthChartFallbackMessage({
  message,
  icon,
  minHeight = 200,
}: {
  message?: ReactNode;
  icon: ReactNode;
  minHeight?: number;
}) {
  return (
    <HStack
      gap={2}
      justifyContent="center"
      alignItems="center"
      flexGrow={1}
      minHeight={minHeight}
      width="100%"
    >
      {icon}
      {message && (
        <TextLabel2 as="p" color="fgMuted">
          {message}
        </TextLabel2>
      )}
    </HStack>
  );
}

export function LiquidityDepthChartLoading({ minHeight = 300 }: { minHeight?: number }) {
  return (
    <VStack gap={2} width="100%" flexGrow={1} minHeight={minHeight}>
      <Fallback width="100%" height="100%" shape="squircle" />
    </VStack>
  );
}

export type LiquidityDepthChartProps = {
  /** Pool key containing token pair info */
  poolKey: PoolKey;
  /** Current pool state with tick info */
  poolState: PoolState;
  /** Which bounds are at their limits (boolean) or tick values (number) */
  tickAtLimit: { [bound in Bound]?: boolean | number };
  /** Whether to invert prices (swap base/quote tokens) */
  invertPrices: boolean;
  /** Brush domain for the selected range [lower, upper] */
  brushDomain?: [number, number];
  /** Current pool price */
  currentPrice: number;
  /** Whether the chart is interactive (allows brush changes) */
  interactive?: boolean;
  /** Height for the chart */
  height?: number;
  /** Callback when brush domain changes */
  onBrushDomainChange?: (domain: [number, number], mode: string | undefined) => void;
  /** Custom zoom levels */
  zoomLevels?: ZoomLevels;
  /** Custom styles for the price line (for read-only mode) */
  priceLineStyles?: {
    opacity?: number;
    strokeWidth?: number;
    stroke?: string;
    fill?: string;
  };
  /** Fallback message when uninitialized */
  uninitializedMessage?: string;
  /** Test ID for the component */
  testID?: string;
};

export const LiquidityDepthChart = memo(function LiquidityDepthChart({
  poolKey,
  poolState,
  tickAtLimit,
  invertPrices,
  brushDomain,
  currentPrice,
  interactive = true,
  height,
  onBrushDomainChange,
  zoomLevels: customZoomLevels,
  priceLineStyles,
  uninitializedMessage = 'Your position will appear here.',
  testID = 'liquidity-chart',
}: LiquidityDepthChartProps) {
  const chainId = useChainId();

  // Get chart data
  const { error, formattedData } = useLiquidityChartData({
    chainId,
    poolKey,
    poolState,
  });

  const formattedDataByCurrency = useMemo(() => {
    if (!formattedData) return [];

    const mapped = formattedData
      .map((d) => ({
        activeLiquidity: d.activeLiquidity,
        tick: d.tick,
        price: invertPrices ? d.price1 : d.price0,
      }))
      .filter((d) => d.price > 1e-10 && d.price < 1e10);
    return [...mapped].sort((a, b) => a.price - b.price);
  }, [formattedData, invertPrices]);

  const zoomLevels = useMemo(() => {
    if (customZoomLevels) return customZoomLevels;

    const RANGE_MULTIPLIER = 0.25;
    return {
      initialMin: 1 - RANGE_MULTIPLIER,
      initialMax: 1 + RANGE_MULTIPLIER,
      min: 0.0001,
      max: 1000.0,
    };
  }, [customZoomLevels]);

  const brushLabelValue = useCallback(
    (side: 'left' | 'right', x: number) => {
      if (!currentPrice) return '';
      const isSorted = !invertPrices;
      if (side === 'left' && tickAtLimit[isSorted ? Bound.LOWER : Bound.UPPER]) {
        return '0';
      }
      if (side === 'right' && tickAtLimit[isSorted ? Bound.UPPER : Bound.LOWER]) {
        return '∞';
      }
      return `${x.toFixed(2)}`;
    },
    [invertPrices, currentPrice, tickAtLimit],
  );

  // Default no-op handler for read-only mode
  const handleBrushChange = useCallback(
    (domain: [number, number], mode: string | undefined) => {
      onBrushDomainChange?.(domain, mode);
    },
    [onBrushDomainChange],
  );

  const isUninitialized = formattedData === undefined && !error;

  return (
    <VStack gap={2} width="100%" flexGrow={1} testID={testID}>
      {isUninitialized ? (
        <DepthChartFallbackMessage
          message={uninitializedMessage}
          icon={<Icon name="chartBar" size="m" active />}
          minHeight={height}
        />
      ) : error ? (
        <DepthChartFallbackMessage
          message="Liquidity data not available."
          icon={<Icon name="chartBar" size="m" active />}
          minHeight={height}
        />
      ) : !formattedData || formattedData.length === 0 || !currentPrice ? (
        <DepthChartFallbackMessage
          message="There is no liquidity data."
          icon={<Icon name="chartBar" size="m" active />}
          minHeight={height}
        />
      ) : (
        <DepthChart
          data={{ series: formattedDataByCurrency, current: currentPrice }}
          height={height}
          margins={{ top: 10, right: 2, bottom: 20, left: 0 }}
          styles={{
            area: {
              selection: '#f5f8ff',
            },
            brush: {
              handle: {
                left: '#0052FF',
                right: '#0052FF',
              },
            },
            priceLine: priceLineStyles ?? {
              strokeWidth: 2,
              stroke: '#0052FF',
              fill: 'none',
            },
            bar: {
              opacity: 1.0,
              stroke: '#89909E',
              fill: '#EEF0F3',
              strokeWidth: '1px',
              shapeRendering: 'crispEdges',
            },
            axis: {
              color: '#5B616E',
            },
          }}
          interactive={interactive}
          brushLabels={brushLabelValue}
          brushDomain={brushDomain}
          onBrushDomainChange={handleBrushChange}
          zoomLevels={zoomLevels}
          ticksAtLimit={tickAtLimit}
        />
      )}
    </VStack>
  );
});
